import React, { ReactElement, useMemo, useState } from 'react';

import { getClassNames, noOp } from '../../../../../utils/util';

import { Poll, PollOption, PollResultResponse } from '../../../../../types/entity.types';

import Button from '../../../../common/button/Button';
import TribePollResults from '../tribe-poll-results/TribePollResults';

import { ReactComponent as EditIcon } from '../../../../../resources/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../../../../resources/icons/trash.svg';
import { ReactComponent as CheckIcon } from '../../../../../resources/icons/check.svg';

import './tribe-poll.scss';

type Props = {
  poll: Poll;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  createReaction?: (value: string) => void;
  deleteReaction?: (value: string) => void;
  switchReaction?: (voteValue: string, value: string) => void;
  post?: boolean;
  pollResults?: PollResultResponse;
  retrieveResults?: (callbackFn: () => void) => void;
};

const TribePoll = ({
  poll,
  onEditClick,
  onDeleteClick,
  createReaction,
  deleteReaction,
  switchReaction,
  post = false,
  pollResults,
  retrieveResults = noOp
}: Props): ReactElement => {
  const [showResults, setShowResults] = useState<boolean>(false);

  const totalVotes: number = useMemo(
    (): number => poll.options.reduce((total: number, option: PollOption): number => total + option.votes, 0),
    [poll.options]
  );

  const handleOptionClick = (option: PollOption): void => {
    if (!post) {
      return;
    }

    if (option.voted) {
      deleteReaction?.(option.value);
      return;
    }

    const optionWithVote: PollOption | undefined = poll.options.find(
      (option: PollOption): boolean => option.voted ?? false
    );

    if (optionWithVote && !poll.allowMultipleSelection) {
      switchReaction?.(optionWithVote.value, option.value);
      return;
    }

    createReaction?.(option.value);
  };

  const handleViewResultsClick = (): void => {
    retrieveResults((): void => setShowResults(true));
  };

  const renderOptions = (): ReactElement[] =>
    poll.options.map((option: PollOption): ReactElement => {
      let votePercentage: number = 0;

      if (option.votes > 0) {
        votePercentage = Math.round((option.votes / totalVotes) * 100);
      }

      return (
        <div className="tribe-poll__option-container" key={option.id}>
          <div
            className={getClassNames('tribe-poll__option', { post, dark: true })}
            style={{ clipPath: `inset(0 ${100 - votePercentage}% 0 0)` }}
            onClick={(): void => handleOptionClick(option)}
          >
            <div className="tribe-poll__option__left">
              <div className="tribe-poll__option__checkbox">{option.voted && <CheckIcon />}</div>
              <p className="tribe-poll__option-text tribe-poll__option-text--light">{option.value}</p>
            </div>
            <p className="tribe-poll__option-text tribe-poll__option-text--light">{votePercentage}%</p>
          </div>
          <div
            className={getClassNames('tribe-poll__option', { post, light: true })}
            style={{ clipPath: `inset(0 0 0 ${votePercentage}%)` }}
            onClick={(): void => handleOptionClick(option)}
          >
            <div className="tribe-poll__option__left">
              <div className="tribe-poll__option__checkbox">{option.voted && <CheckIcon />}</div>
              <p className="tribe-poll__option-text tribe-poll__option-text--dark">{option.value}</p>
            </div>
            <p className="tribe-poll__option-text tribe-poll__option-text--dark">{votePercentage}%</p>
          </div>
        </div>
      );
    });

  return (
    <>
      <div className={getClassNames('tribe-poll', { post })} data-testid="tribe-poll">
        <div className="tribe-poll__header">
          <p className="tribe-poll__question">{poll.question}</p>
          <div className="tribe-poll__icon-container">
            {onEditClick && <EditIcon onClick={onEditClick} data-testid="tribe-poll-edit-icon" />}
            {onDeleteClick && <TrashIcon onClick={onDeleteClick} data-testid="tribe-poll-trash-icon" />}
          </div>
        </div>
        <div className="tribe-poll__options">{renderOptions()}</div>
        <div className="tribe-poll__votes">
          <p>{totalVotes} votes</p>
        </div>
        {post && <Button text="VIEW RESULTS" onClick={handleViewResultsClick} />}
      </div>

      <TribePollResults
        poll={poll}
        results={pollResults}
        show={showResults}
        close={(): void => setShowResults(false)}
      />
    </>
  );
};

export default TribePoll;
